import { useEffect } from "react";
import { Platform } from "react-native";
import DeviceInfo from "react-native-device-info";
import notifee, { AndroidImportance, EventType } from "@notifee/react-native";
import { useUserStore } from "../store/userStore";
import { registerDevice } from "../services/apiService";
import { showNotification } from "../services/notificationService";
import { useDeeplinkStore } from "../store/deeplinkStore";
import { trackInteraction, trackIdentify } from "../analytics/analyticsUtil";
import { ACTION_TYPE, ANALYTICS_EVENT } from "../analytics/types";
import { ROUTES } from "../shared/constants/routes";

export function useFcmLifecycle() {
  const firebaseUser = useUserStore((s) => s.user?.firebaseUser);
  const initialized = useUserStore((s) => s.initialized);

  /**
   * One-time notification environment setup
   */
  useEffect(() => {
    if (Platform.OS === "web") return;

    let fgUnsub: () => void;
    let tokenRefreshUnsub: () => void;
    let openedUnsub: () => void;
    let pressUnsub: () => void;
    const pushLink = (link?: string) => {
      if (link) {
        trackInteraction({ event: ANALYTICS_EVENT.INTERACTION, action: ACTION_TYPE.CLICK, screen_name: ROUTES.HOME, component: "notification" });
        useDeeplinkStore.getState().pushUrl(link);
      }
    };

    async function bootstrap() {
      if (Platform.OS === "web") return null;
      const { default: messaging } = await import(
        "@react-native-firebase/messaging"
      );

      // Permission
      await notifee.requestPermission();

      // Channel
      await notifee.createChannel({
        id: "default",
        name: "Default",
        importance: AndroidImportance.HIGH,
      });

      // Background handler
      messaging().setBackgroundMessageHandler(async (notify) => {
        if (notify?.notification) return;
        return showNotification(notify);
      });

      // Foreground message
      fgUnsub = messaging().onMessage(showNotification);

      // Notification click
      pressUnsub = notifee.onForegroundEvent(({ type, detail }) => {
        const link = detail?.notification?.data?.link;
        if (type === EventType.PRESS) {
          pushLink(link);
        }
      });

      openedUnsub = messaging().onNotificationOpenedApp((message) => {
        const link = message?.data?.link ?? message?.notification?.data?.link;
        pushLink(link);
      });

      // Open from killed state
      const initialMessagingNotification =
        await messaging().getInitialNotification();
      const initialLink =
        initialMessagingNotification?.data?.link ??
        initialMessagingNotification?.notification?.data?.link;
      pushLink(initialLink);

      // Token refresh
      tokenRefreshUnsub = messaging().onTokenRefresh(async (token) => {
        const deviceId = await DeviceInfo.getUniqueId();
        await registerDevice(deviceId, token);
      });
    }

    bootstrap();

    return () => {
      fgUnsub?.();
      tokenRefreshUnsub?.();
      openedUnsub?.();
      pressUnsub?.();
    };
  }, []);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    if (!initialized) return;
    if (!firebaseUser?.uid) return;

    async function syncWebDevice() {
      try {
        let deviceId = localStorage.getItem('device_id');
        if (!deviceId) {
          deviceId = crypto.randomUUID();
          localStorage.setItem('device_id', deviceId);
        }
        await registerDevice(deviceId, '');
      } catch (e) {
        console.log("Web device sync failed:", e);
      }
    }

    syncWebDevice();
  }, [firebaseUser?.uid, initialized]);
  /**
   * 🔁 Re-link device when UID changes
   */
  useEffect(() => {
    if (Platform.OS === "web") return;
    if (!initialized) return;
    if (!firebaseUser) return;
    if (!firebaseUser.uid) return;
    let cancelled = false;

    async function syncDevice() {
      try {
        if (Platform.OS === "web") return null;
        const { default: messaging } = await import(
          "@react-native-firebase/messaging"
        );

        const token = await messaging().getToken();
        if (!token) return;

        const deviceId = await DeviceInfo.getUniqueId();

        if (!cancelled) {
          await registerDevice(deviceId, token);
          trackIdentify(firebaseUser.uid, { device_id: deviceId });
        }
      } catch (e) {
        console.log("Device sync failed:", e);
      }
    }

    syncDevice();

    return () => {
      cancelled = true;
    };
  }, [firebaseUser?.uid, initialized]);
}